import React, { useState, useEffect, useRef } from 'react';
import { Send, X, User, LogOut } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import toast from 'react-hot-toast';
import { useWebSocket } from '../../contexts/WebSocketContext';
import { showRealtimeNotificationToast } from '../../utils/realtimeNotificationToast';

const CUSTOMER_LEFT_FALLBACK =
  'The customer has left the chat. Close this panel when you are ready — you remain online for the next request.';

/**
 * Agent Chat Interface Component
 * Real-time chat interface for agents to chat with customers
 * Uses persistent WebSocket connection from WebSocketContext
 */
const ChatInterface = ({ chatSession, onClose, floating = false }) => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [typingIndicator, setTypingIndicator] = useState(null);
  const [chatSessionEnded, setChatSessionEnded] = useState(false);
  /** True when the customer ended the chat (not when the agent ended it from their side). */
  const [customerLeft, setCustomerLeft] = useState(false);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const remoteEndToastShownRef = useRef(false);
  const currentUser = JSON.parse(sessionStorage.getItem('userDetails') || '{}');
  const { isConnected, connectionStatus, subscribeToChatSession, sendChatMessage, sendTypingIndicator } = useWebSocket();

  // Subscribe to chat session messages using persistent WebSocket connection
  useEffect(() => {
    // Ensure chatSessionId is available
    if (!chatSession?.chatSessionId) {
      console.warn('Chat Session ID not available. Cannot subscribe to chat session.');
      return;
    }

    if (!isConnected) {
      console.warn('WebSocket not connected. Waiting for connection...');
      return;
    }

    const chatSessionId = chatSession.chatSessionId;
    //console.log('Subscribing to chat session:', chatSessionId);

    // Subscribe to messages for this chat session
    const unsubscribe = subscribeToChatSession(chatSessionId, (messageData) => {
      //console.log('Agent received chat message:', messageData);

      if (messageData.messageType === 'MESSAGE' || messageData.type === 'CHAT_MESSAGE') {
        setMessages((prev) => {
          const existsById = prev.find((msg) => msg.messageId === messageData.messageId);
          if (existsById) return prev;

          // Check by content + sender + timestamp (within 2 seconds) to prevent duplicates
          const existsByContent = prev.find((msg) => {
            const timeDiff = Math.abs((msg.timestamp || 0) - (messageData.timestamp || 0));
            return msg.content === messageData.content &&
                   msg.senderType === messageData.senderType &&
                   msg.senderName === messageData.senderName &&
                   timeDiff < 2000;
          });
          if (existsByContent) return prev;
          
          return [...prev, messageData];
        });
        setTypingIndicator(null);
      } else if (messageData.messageType === 'TYPING' && messageData.senderType === 'GUEST') {
        setTypingIndicator({
          senderName: messageData.senderName,
          senderType: messageData.senderType,
        });
        if (typingTimeoutRef.current) {
          clearTimeout(typingTimeoutRef.current);
        }
        typingTimeoutRef.current = setTimeout(() => {
          setTypingIndicator(null);
        }, 3000);
      } else if (messageData.messageType === 'JOINED') {
        setMessages((prev) => {
          const exists = prev.find((msg) => msg.messageId === messageData.messageId);
          if (exists) return prev;
          return [...prev, messageData];
        });
      } else if (messageData.messageType === 'LEFT' || messageData.messageType === 'ENDED' || messageData.messageType === 'CLOSED') {
        const endedByAgent = messageData.senderType === 'AGENT';
        setChatSessionEnded(true);
        if (!endedByAgent) {
          setCustomerLeft(true);
        }
        const lineBody = endedByAgent
          ? (messageData.content && String(messageData.content).trim()) ||
            'This chat session has been ended.'
          : (messageData.content && String(messageData.content).trim()) || CUSTOMER_LEFT_FALLBACK;
        setMessages((prev) => {
          const exists = prev.find((msg) => msg.messageId === messageData.messageId);
          if (exists) return prev;
          return [...prev, {
            ...messageData,
            messageType: 'LEFT',
            senderType: endedByAgent ? 'AGENT' : 'SYSTEM',
            senderName: endedByAgent
              ? (messageData.senderName || 'Agent')
              : (messageData.senderName || 'Customer left'),
            content: lineBody,
          }];
        });
        if (!endedByAgent && !remoteEndToastShownRef.current) {
          remoteEndToastShownRef.current = true;
          showRealtimeNotificationToast('Customer left', lineBody, {
            dedupeKey: chatSessionId ? `agent_chat_end:${chatSessionId}` : undefined,
          });
        }
      }
    });

    unsubscribeRef.current = unsubscribe;

    // Add initial "Agent joined" message if this is a new session
    if (chatSession.agentName) {
      const joinMessage = {
        messageId: `join-${Date.now()}`,
        senderId: chatSession.assignedAgentId?.toString(),
        senderName: chatSession.agentName,
        senderType: 'AGENT',
        content: `Agent ${chatSession.agentName} has joined the chat. How can I help you?`,
        messageType: 'JOINED',
        timestamp: Date.now(),
      };
      setMessages([joinMessage]);
    }

    // Cleanup: Unsubscribe when component unmounts or chat session changes
    // NOTE: This does NOT disconnect the WebSocket - it only unsubscribes from this chat session
    return () => {
      //console.log('Unsubscribing from chat session:', chatSessionId);
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Clear messages when leaving chat session
      setMessages([]);
      setTypingIndicator(null);
      setChatSessionEnded(false);
      setCustomerLeft(false);
      remoteEndToastShownRef.current = false;
    };
  }, [chatSession?.chatSessionId, isConnected, subscribeToChatSession]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typingIndicator]);

  /**
   * Handle sending a message
   */
  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || !chatSession?.chatSessionId) {
      return;
    }

    if (chatSessionEnded) {
      toast.error('This chat has ended. Close the panel to handle new requests.');
      return;
    }

    if (!isConnected) {
      toast.error('Not connected. Please wait...');
      return;
    }

    const messageContent = inputMessage.trim();
    setInputMessage('');

    const senderName = currentUser?.username || currentUser?.name || 'Agent';
    const senderId = currentUser?.userId || currentUser?.id || null;

    // Send message via persistent WebSocket connection
    const success = sendChatMessage(
      chatSession.chatSessionId,
      messageContent,
      'AGENT',
      senderName,
      senderId
    );

    if (success) {
      // Add message to local state immediately for better UX
      setMessages((prev) => [...prev, {
        chatSessionId: chatSession.chatSessionId,
        content: messageContent,
        senderType: 'AGENT',
        senderName: senderName,
        senderId: senderId,
        messageType: 'MESSAGE',
        messageId: `temp-${Date.now()}`,
        timestamp: Date.now(),
      }]);
    } else {
      toast.error('Failed to send message. Please try again.');
      setInputMessage(messageContent); // Restore message if failed
    }
  };

  /**
   * Handle typing indicator
   */
  const handleTyping = () => {
    if (!isConnected) {
      return;
    }

    if (!isTyping && inputMessage.trim()) {
      setIsTyping(true);
      const senderName = currentUser?.username || currentUser?.name || 'Agent';
      const senderId = currentUser?.userId || currentUser?.id || null;
      
      sendTypingIndicator(
        chatSession.chatSessionId,
        'AGENT',
        senderName,
        senderId
      );
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  /**
   * Format timestamp for display
   */
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return '';
    }
  };


  if (!chatSession) {
    return null;
  }

  return (
    <Card
      className={
        floating
          ? 'flex flex-col h-[min(560px,70vh)] max-h-[70vh] shadow-2xl border-border-light dark:border-border-dark'
          : 'flex flex-col h-[600px]'
      }
    >
      <CardHeader className="flex-shrink-0 border-b border-border-light dark:border-border-dark">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
              <User className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-lg">{chatSession.guestName || 'Guest'}</CardTitle>
                {chatSessionEnded && customerLeft && (
                  <Badge className="bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100 border-amber-300 dark:border-amber-700">
                    <LogOut className="h-3 w-3 mr-1 inline" />
                    Customer left
                  </Badge>
                )}
              </div>
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {chatSession.subject || 'Chat Session'}
                {connectionStatus !== 'connected' && (
                  <span className="ml-2 text-orange-500">({connectionStatus})</span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              onClick={onClose}
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              title="Close Chat Window"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        {chatSession.ticketNumber && (
          <div className="mt-2">
            <Badge variant="outline" className="text-xs">
              Ticket: {chatSession.ticketNumber}
            </Badge>
          </div>
        )}
        {chatSessionEnded && customerLeft && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            <strong className="font-semibold">Customer has left the chat.</strong>{' '}
            Close this panel when you are finished — you stay online and will receive the next chat request here.
          </div>
        )}
      </CardHeader>

      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.messageId || `msg-${message.timestamp}`}
              className={`flex ${
                message.senderType === 'SYSTEM'
                  ? 'justify-center'
                  : message.senderType === 'AGENT'
                    ? 'justify-end'
                    : 'justify-start'
              }`}
            >
              <div
                className={`max-w-[70%] rounded-lg px-4 py-2 ${
                  message.senderType === 'AGENT'
                    ? 'bg-blue-500 text-white'
                    : message.senderType === 'SYSTEM'
                      ? 'border border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-800 dark:bg-amber-950/50 dark:text-amber-50'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                }`}
              >
                {message.senderType === 'GUEST' && (
                  <div className="text-xs font-semibold mb-1 opacity-75">
                    {message.senderName || 'Guest'}
                  </div>
                )}
                {message.senderType === 'SYSTEM' && (
                  <div className="text-xs font-semibold mb-1 opacity-90">
                    {message.messageType === 'LEFT' ? (message.senderName || 'Customer left') : (message.senderName || 'System')}
                  </div>
                )}
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                <p className="text-xs mt-1 opacity-75">
                  {formatTime(message.timestamp)}
                </p>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {typingIndicator && typingIndicator.senderType === 'GUEST' && (
            <div className="flex justify-start">
              <div className="bg-gray-200 dark:bg-gray-700 rounded-lg px-4 py-2">
                <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                  {typingIndicator.senderName || 'Guest'} is typing...
                </p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="border-t border-border-light dark:border-border-dark p-4 flex-shrink-0">
          <form onSubmit={handleSendMessage} className="flex items-center gap-2">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => {
                setInputMessage(e.target.value);
                handleTyping();
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage(e);
                }
              }}
              placeholder={chatSessionEnded ? 'Chat ended — use Close to dismiss' : 'Type your message...'}
              disabled={chatSessionEnded}
              className="flex-1 px-4 py-2 border border-border-light dark:border-border-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white disabled:opacity-60"
            />
            <Button
              type="submit"
              disabled={chatSessionEnded || !inputMessage.trim() || connectionStatus !== 'connected'}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default ChatInterface;
